import { getCalendarStore } from "../master/store/calendarStoreRegistry";
import { applyOptimisticUpsert } from "../master/mutations/calendarMutationSync";
import { ultraSessionLog } from "../debug/ultraSessionDebug";
import {
   buildOptimisticSlotVisualItem,
   buildReservationStatusMeta,
   normalizeSlotVisualColor,
} from "./buildOptimisticSlotVisualItem";
import {
   getReservationColumnInstructorId,
   getReservationStudentId,
   reservationIdFromAny,
   reservationStartFromAny,
} from "../ultra/reservationRowUtils";
import { normalizeColorToCss } from "../../components/APanel/CalendarExtreme/extremeEventColors";
import {
   composeReservationAndProfileNote,
   getPersonFullName,
   getPersonPhone,
   getPersonPrivateMessage,
   getReservationColor,
   getReservationGearbox,
   getReservationIsCanceled,
   getReservationIsFavorite,
   getReservationIsImportant,
   getReservationPhone,
   getReservationPrivateMessage,
   getReservationTags,
   resolveGearboxBadge,
} from "../../components/APanel/CalendarExtreme/extremeReservationRows";
import {
   createHasReservationSubstitution,
   createSubstitutionOverlayContext,
} from "../substitution/substitutionOverlayLookup";

function resolveHasReservationSubstitution(context = {}) {
   if (typeof context.hasReservationSubstitution === "function") {
      return context.hasReservationSubstitution;
   }
   const overlayCtx =
      context.substitutionOverlayContext ||
      createSubstitutionOverlayContext({
         substitutionRows: context.substitutionRows,
         substituteInstructorById: context.substituteInstructorById,
         substitutionRowsReady: context.substitutionRowsReady,
      });
   return createHasReservationSubstitution(
      overlayCtx,
      context.getReservationStartRaw,
   );
}

function normalizeHourMinuteLabel(value) {
   const m = String(value || "")
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);
   if (!m) return "";
   return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
}

function resolvePersonFromRow(row, context = {}) {
   const studentIdRaw = getReservationStudentId(row);
   const studentId =
      studentIdRaw == null ? "" : String(studentIdRaw).trim();
   const usersById = context.usersById;

   if (studentId && usersById instanceof Map) {
      const fromMap = usersById.get(studentId);
      if (fromMap) return fromMap;
   }
   if (studentId && usersById && typeof usersById === "object") {
      const fromObj = usersById[studentId];
      if (fromObj) return fromObj;
   }

   return (
      row?.student ||
      row?.user ||
      row?.reservation?.student ||
      row?.reservation?.user ||
      null
   );
}

export function buildVisualItemFromReservationRow(row, context = {}) {
   const sourceRow = row;
   const hasSubstitution = resolveHasReservationSubstitution(context);
   const id = reservationIdFromAny(sourceRow);
   if (!id) return null;

   const person = resolvePersonFromRow(sourceRow, context);
   const userId = getReservationStudentId(sourceRow);
   const instructorId = getReservationColumnInstructorId(sourceRow);
   const start = reservationStartFromAny(sourceRow);
   const timeFromFormat =
      typeof context.formatTimeLabelInChisinau === "function" && start
         ? context.formatTimeLabelInChisinau(start)
         : "";
   const timeText = normalizeHourMinuteLabel(
      timeFromFormat || sourceRow?.timeText || "",
   );

   const title =
      getPersonFullName(person) ||
      String(
         sourceRow?.title ??
            sourceRow?.studentName ??
            sourceRow?.fullName ??
            sourceRow?.name ??
            sourceRow?.reservation?.title ??
            sourceRow?.reservation?.name ??
            context.title ??
            "",
      ).trim() ||
      "Rezervare";

   const phone =
      getPersonPhone(person) ||
      getReservationPhone(sourceRow) ||
      String(context.phone || "").trim();

   const reservationNote = getReservationPrivateMessage(sourceRow);
   const profileNote = getPersonPrivateMessage(person);
   const displayNote = composeReservationAndProfileNote(
      reservationNote,
      profileNote,
   );

   const gearbox = getReservationGearbox(sourceRow);
   const isFavorite = getReservationIsFavorite(sourceRow);
   const isImportant = getReservationIsImportant(sourceRow);
   const isCanceled = getReservationIsCanceled(sourceRow);
   const normalizeColor =
      typeof context.normalizeColorToCss === "function"
         ? context.normalizeColorToCss
         : normalizeColorToCss;
   const color =
      getReservationColor(sourceRow, person) ||
      normalizeColor(sourceRow?.color);
   const backendTags = getReservationTags(sourceRow);
   const gearboxBadge = resolveGearboxBadge(gearbox);
   const statusMarks = [];
   if (hasSubstitution(sourceRow)) statusMarks.push("§");
   const mergedTags = [...backendTags];
   if (gearboxBadge) mergedTags.push(gearboxBadge);
   const metaSeed = [mergedTags.join(" · "), sourceRow?.meta]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" · ");
   const meta = buildReservationStatusMeta({
      meta: metaSeed,
      isFavorite,
      isImportant,
      gearbox,
      extraTags: statusMarks,
   });

   return buildOptimisticSlotVisualItem({
      id,
      userId,
      instructorId,
      title,
      phone,
      note: displayNote,
      privateMessage: reservationNote,
      color,
      isFavorite,
      isImportant,
      isCanceled,
      sector: String(
         sourceRow?.sector || sourceRow?.reservation?.sector || "",
      ).trim(),
      gearbox,
      hasSectorMismatch: sourceRow?.hasSectorMismatch === true,
      timeText,
      meta,
   });
}

/**
 * Instant grid paint for create/edit/socket — store + patch bus.
 */
export function applyReservationGridPatch({
   reservation,
   slotKey = "",
   visualItem = null,
   calendarStore = null,
   patchBus = null,
   context = {},
} = {}) {
   const store = calendarStore || getCalendarStore();
   if (!store || !reservation) return false;

   const id = reservationIdFromAny(reservation);
   if (!id) return false;

   const key =
      String(slotKey || "").trim() ||
      String(store.getEventSlotKey?.(id) || "").trim() ||
      String(
         typeof context.resolveSlotKey === "function"
            ? context.resolveSlotKey(reservation)
            : "",
      ).trim();
   if (!key) return false;

   const item =
      visualItem ||
      buildVisualItemFromReservationRow(reservation, context) ||
      null;
   if (!item) return false;

   const bus = patchBus || store.patchBus || null;
   const resolvedVisualItem = {
      ...item,
      id: String(id),
      color: normalizeSlotVisualColor(item.color),
   };
   const applied = applyOptimisticUpsert(store, {
      event: { ...reservation, id },
      slotKey: key,
      patchBus: bus,
      visualItem: resolvedVisualItem,
   });
   if (applied) {
      ultraSessionLog("MUTATION", "edit:gridPatch", {
         id: String(id),
         slotKey: key,
         title: resolvedVisualItem.title || null,
         phone: resolvedVisualItem.phone || null,
         color: resolvedVisualItem.color || null,
         isFavorite: resolvedVisualItem.isFavorite === true,
         isImportant: resolvedVisualItem.isImportant === true,
         meta: resolvedVisualItem.meta || "",
      });
   }
   return applied;
}
